/**
 * Service d'authentification frontend
 */

const API_BASE_URL = '/api/auth';

/** Admins autorisés à réinitialiser le MFA (aligné serveur MFA_RESET_ADMIN_EMAILS) */
export const MFA_RESET_ADMIN_EMAILS = [
  'jean-michel.caouette@bzinc.ca',
  'dany.bourassa@bzinc.ca',
  'vincent.bourassa@bzinc.ca',
  'eric.hains@bzinc.ca',
  'eric.durand@bzinc.ca',
] as const;

/**
 * client_id des clients soumis au MFA (aligné serveur MFA_CLIENT_IDS).
 * 1 SERQ, 2 BZ, 9 Coeficia, 12 Atlantis Pompe, 13 DFK, 14 Château Laurier,
 * 15 Unik, 18 Tremblay Bois, 19 Ambioner, 30 Prisme Modèle, 36 EGR, 40 GDA, 41 Tibo, 43 AMFQ, 45 APE,
 * 50 Beauvais Truchon, 53 3DRPD, 63 LBCO, 81 RMB, 82 MMAQ
 */
export const MFA_PILOT_CLIENT_IDS = [1, 2, 9, 12, 13, 14, 15, 18, 19, 30, 36, 40, 41, 43, 45, 50, 53, 63, 81, 82] as const;

export function canManageMfaReset(adminEmail: string | undefined | null): boolean {
  const normalized = (adminEmail || '').trim().toLowerCase();
  return (MFA_RESET_ADMIN_EMAILS as readonly string[]).includes(normalized);
}

/** Partie prenante soumise au MFA (clients pilotes). */
export function isMfaSubjectPartiePrenante(pp: {
  client_id?: number | string | null;
  is_user?: boolean | null;
}): boolean {
  const clientId =
    typeof pp.client_id === 'string' ? parseInt(pp.client_id, 10) : Number(pp.client_id);
  return (MFA_PILOT_CLIENT_IDS as readonly number[]).includes(clientId) && pp.is_user === true;
}

export interface User {
  id: number;
  email: string;
  nom: string;
  prenom: string;
  titre_poste?: string;
  organisation?: string;
  photo_url?: string | null;
  acces_prisme_master?: boolean;
}

export type LoginResult =
  | { status: 'authenticated'; user: User }
  | {
      status: 'mfa_required';
      mfaToken: string;
      user: User;
      expiresInMinutes: number;
      maxAttempts: number;
    }
  | {
      status: 'mfa_setup_required';
      setupToken: string;
      otpauthUrl: string;
      user: User;
      expiresInMinutes: number;
      maxAttempts: number;
    }
  | { status: 'error'; error: string; attemptsRemaining?: number; locked?: boolean };

async function parseJsonResponse(response: Response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { error: text };
  }
}

/**
 * Connexion (étape mot de passe — peut exiger MFA ensuite)
 */
export async function login(email: string, password: string): Promise<LoginResult> {
  try {
    const response = await fetch(`${API_BASE_URL}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });

    const data = await parseJsonResponse(response);

    if (!response.ok) {
      return { status: 'error', error: data.error || `Erreur ${response.status}` };
    }

    if (data.mfaRequired && data.mfaToken) {
      return {
        status: 'mfa_required',
        mfaToken: data.mfaToken,
        user: data.user,
        expiresInMinutes: data.expiresInMinutes ?? 5,
        maxAttempts: data.maxAttempts ?? 5,
      };
    }

    if (data.mfaSetupRequired && data.setupToken) {
      return {
        status: 'mfa_setup_required',
        setupToken: data.setupToken,
        otpauthUrl: data.otpauthUrl,
        user: data.user,
        expiresInMinutes: data.expiresInMinutes ?? 5,
        maxAttempts: data.maxAttempts ?? 5,
      };
    }

    if (data.success && data.user) {
      return { status: 'authenticated', user: data.user };
    }

    return { status: 'error', error: data.error || 'Réponse de connexion inattendue' };
  } catch (error) {
    return {
      status: 'error',
      error: error instanceof Error ? error.message : 'Erreur de connexion',
    };
  }
}

export async function confirmMfaSetup(setupToken: string, code: string): Promise<LoginResult> {
  try {
    const response = await fetch(`${API_BASE_URL}/mfa/setup/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ setupToken, code }),
    });
    const data = await parseJsonResponse(response);
    if (!response.ok) {
      return {
        status: 'error',
        error: data.error || 'Code invalide',
        attemptsRemaining: data.attemptsRemaining,
        locked: data.locked,
      };
    }
    if (data.success && data.user) {
      return { status: 'authenticated', user: data.user };
    }
    return { status: 'error', error: data.error || 'Erreur configuration MFA' };
  } catch (error) {
    return {
      status: 'error',
      error: error instanceof Error ? error.message : 'Erreur configuration MFA',
    };
  }
}

export async function verifyMfaLogin(mfaToken: string, code: string): Promise<LoginResult> {
  try {
    const response = await fetch(`${API_BASE_URL}/mfa/login/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ mfaToken, code }),
    });
    const data = await parseJsonResponse(response);
    if (!response.ok) {
      return {
        status: 'error',
        error: data.error || 'Code invalide',
        attemptsRemaining: data.attemptsRemaining,
        locked: data.locked,
      };
    }
    if (data.success && data.user) {
      return { status: 'authenticated', user: data.user };
    }
    return { status: 'error', error: data.error || 'Erreur vérification MFA' };
  } catch (error) {
    return {
      status: 'error',
      error: error instanceof Error ? error.message : 'Erreur vérification MFA',
    };
  }
}

export interface MfaPilotStatus {
  id: number;
  email: string;
  prenom: string;
  nom: string;
  mfaEnabled: boolean;
  pendingSetup: boolean;
  enabledAt: string | null;
}

export async function fetchMfaAdminStatus(): Promise<MfaPilotStatus[]> {
  const response = await fetch(`${API_BASE_URL}/admin/mfa/pilot-status`, { credentials: 'include' });
  const data = await parseJsonResponse(response);
  if (!response.ok) throw new Error(data.error || 'Impossible de charger le statut MFA');
  return data.pilots || [];
}

/** @deprecated Utiliser fetchMfaAdminStatus */
export const fetchMfaPilotStatus = fetchMfaAdminStatus;

export async function adminResetMfa(input: { email?: string; userId?: number } | string): Promise<void> {
  const payload =
    typeof input === 'string'
      ? { email: input }
      : { email: input.email, userId: input.userId };
  const response = await fetch(`${API_BASE_URL}/admin/mfa/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok) throw new Error(data.error || 'Échec réinitialisation MFA');
}

export async function logout(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/logout`, {
      method: 'POST',
      credentials: 'include',
    });
    const data = await response.json();
    return data.success === true;
  } catch {
    return false;
  }
}

export async function checkAuth(): Promise<User | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/me`, { credentials: 'include' });
    if (!response.ok) {
      if (response.status === 404 || response.status === 401) return null;
      return null;
    }
    const data = await response.json();
    return data.user || null;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      return null;
    }
    return null;
  }
}

export async function refreshToken(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    const data = await response.json();
    return data.success === true;
  } catch {
    return false;
  }
}
